import React, { useState } from 'react';
import { withStyles, Paper, LinearProgress, Dialog, Link, Fab } from '@material-ui/core';
import { Storage } from 'aws-amplify';
import GraphqlApi from '../../../api-graphql';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../../UI/AlertDialog';
import UploadForm from '../../UploadForm';
import { AddOutlined } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CONSTANTS } from '../../../constants/constants';

const styles = theme => ({
  paper: {
    maxWidth: '50%',
    margin: 'auto',
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(6))]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(6)
    },
    [theme.breakpoints.down(600 + theme.spacing(6))]: {
      maxWidth: '100%'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    fontSize: '1em'
  },
  input: {
    width: '100%'
  },
  formControl: {
    width: '100%',
    marginTop: 16,
    marginBottom: 8
  },
  button: {
    backgroundColor: theme.palette.primary.main
  },
  btn: {
    backgroundColor: theme.palette.primary.main,
    marginRight: '1rem',
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    },
    '& span svg': {
      color: 'white'
    }
  },
  addBtn: {
    marginLeft: '15px',
    padding: '3rem 0',
    '& a': {
      color: theme.palette.text.secondary,
      textTransform: 'uppercase'
    },
    '& a:hover': {
      textDecoration: 'none'
    },
    '& a button span svg': {
      color: 'white'
    },
    [theme.breakpoints.down('xs')]: {
      marginLeft: '15px'
    }
  }
});

function generatePathS3 (folder, ext, user, group) {
  const timestamp = Date.now();
  const usernameNormalized = user.username
    .replace(/\s/g, '_')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const id = `${timestamp}_${usernameNormalized}`;
  return `document/${group}/${folder}/${id}${ext}`;
}

const UploadAnnexes = (props) => {
  const { classes, document, setDocument, user, group, getData } = props;
  const [files, setFiles] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const FILE_TYPES_ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
  const { t } = useTranslation();

  const currentFiles = [document.principal];
  if (document.annexes) currentFiles.push(...document.annexes);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      setOpen(false);
      document.updatedAt = new Date().toISOString();

      const name = e.target.name.value;
      const description = e.target.description.value || '';
      const ext = /[.]\w{3,4}$/.exec(files[0].name)[0];

      if (!ext || !FILE_TYPES_ACCEPTED.includes(ext)) {
        const error = new Error('Wrong Extension');
        error.customMessage = `${t('uploadAnnexes:extensionError')} ${FILE_TYPES_ACCEPTED}`;
        throw error;
      }

      const path = generatePathS3(document.id, ext, user, group);
      const annexe = { name, path, description };
      await Storage.put(path, files[0]);
      document.annexes !== null
        ? document.annexes.push(annexe)
        : document.annexes = [annexe];
      let response = await GraphqlApi.updateDocument(document);
      setDocument(response.data.updateDocument);
      setLoading(false);
      getData();
    } catch (e) {
      console.error('Error creating PDF', e);
      setError(e.customMessage || t('uploadAnnexes:genericError'));
      setOpen(false);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 0, position: 'absolute', left: 0, width: '100%', zIndex: 9999, textAlign: 'center' }}>
        <LinearProgress />
      </div>
    );
  }

  return (
    <>
      <AlertDialog open={!!error} title='Error' description={error} setOpen={() => setError(null)} />
      {
        document.status === CONSTANTS.DOCUMENT.STATUS.INIT && document.type &&
        <div className={classes.addBtn}>
          <Link variant='body1' underline='none'>
            <Fab
              to='/document/new'
              className={classes.btn}
              aria-label='Add'
              onClick={() => setOpen(true)}
            >
              <AddOutlined />
            </Fab>
            {t('uploadAnnexes:addAttachment')}
          </Link>
        </div>
      }
      {
        open &&
        <Dialog open={open} fullScreen>
          <Paper className={classes.paper}>
            <UploadForm
              newDocument
              handleSubmit={handleSubmit}
              handleCancel={() => setOpen(false)}
              setContent={setFiles}
              accept={FILE_TYPES_ACCEPTED.join(',')}
              title={t('uploadAnnexes:createNewDoc')}
              currentFiles={currentFiles}
            />
          </Paper>
        </Dialog>
      }
    </>
  );
};

UploadAnnexes.propTypes = {
  classes: PropTypes.object.isRequired,
  document: PropTypes.object.isRequired,
  setDocument: PropTypes.func.isRequired,
  newDocument: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    user: state.config.user,
    group: state.config.group,
    subgroup: state.config.subgroup
  };
};

export default withStyles(styles, { withTheme: true })(connect(mapStateToProps, null)(UploadAnnexes));
